import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const TRANSLATIONS = {
  ru: {
    title: 'Проверка возраста',
    subtitle: 'Это приложение предназначено только для пользователей старше 18 лет',
    warningPrefix: 'Внимание:',
    warningText: 'Содержимое может включать материалы для взрослых. Продолжая, вы подтверждаете, что достигли совершеннолетия.',
    confirmButton: 'Да, мне больше 18 лет',
    confirmingButton: 'Подтверждение...',
    declineButton: 'Мне меньше 18 лет',
    errorText: 'Подтверждение возраста обязательно для использования приложения',
    confirmRequirementsTitle: 'Нажимая "Да", вы подтверждаете, что:',
    confirmRequirementItems: [
      'Вам исполнилось 18 лет или больше',
      'Вы согласны с условиями использования',
      'Понимаете характер контента',
    ],
    disclaimerTitle: 'Отказ от ответственности',
    disclaimerIntro: 'Пожалуйста, внимательно ознакомьтесь с условиями перед продолжением.',
    disclaimerItems: [
      {
        title: 'Возрастное ограничение',
        text: 'Данный ресурс (Telegram-бот) содержит материалы строго 18+ (только для взрослых). Продолжая использование, вы подтверждаете, что достигли совершеннолетнего возраста.',
      },
      {
        title: 'Характер контента',
        text: 'Весь размещенный здесь графический и текстовый контент, включая фото- и видеоматериалы, является 100% сгенерированным искусственным интеллектом (AI-Generated).',
      },
      {
        title: 'Отсутствие реальности',
        text: 'Изображенные персонажи и сюжеты являются вымышленными, не имеют отношения к реальным людям и созданы исключительно с помощью компьютерных алгоритмов. Материалы не изображают и не используют реальных лиц, в том числе несовершеннолетних.',
      },
      {
        title: 'Цель использования',
        text: 'Материалы предназначены исключительно для художественного, развлекательного и обучающего ознакомления лиц, достигших 18 лет. Владелец ресурса не несет ответственности за любое противозаконное использование или распространение данного контента пользователями.',
      },
      {
        title: 'Запрет на незаконное использование',
        text: 'Категорически запрещено любое использование контента, нарушающее законодательство вашей страны, включая, но не ограничиваясь, распространение детской порнографии, пропаганду насилия и противозаконную деятельность.',
      },
    ],
    disclaimerContinue: 'Я ознакомился и продолжаю',
    disclaimerBack: 'Назад',
    langRu: 'RU',
    langEn: 'EN',
  },
  en: {
    title: 'Age Verification',
    subtitle: 'This application is intended only for users aged 18 or older.',
    warningPrefix: 'Attention:',
    warningText: 'The content may include adult material. By continuing you confirm you are of legal age.',
    confirmButton: "Yes, I'm over 18",
    confirmingButton: 'Confirming...',
    declineButton: "I'm under 18",
    errorText: 'Age confirmation is required to use the application',
    confirmRequirementsTitle: 'By pressing "Yes" you confirm that:',
    confirmRequirementItems: [
      'You are 18 years old or older',
      'You agree to the terms of use',
      'You understand the nature of the content',
    ],
    disclaimerTitle: 'Disclaimer',
    disclaimerIntro: 'Please carefully review the following terms before continuing.',
    disclaimerItems: [
      {
        title: 'Age Restriction',
        text: 'This resource (Telegram bot) contains strictly 18+ material (adults only). By continuing you confirm that you are of legal age.',
      },
      {
        title: 'Nature of Content',
        text: 'All images, videos, and text content available here are 100% AI-generated.',
      },
      {
        title: 'Fictional Characters',
        text: 'All depicted characters and storylines are fictional, have no relation to real people, and are created solely via computer algorithms. No real individuals, including minors, are depicted or used.',
      },
      {
        title: 'Purpose of Use',
        text: 'The materials are intended strictly for artistic, entertainment, and educational viewing by adults. The resource owner is not responsible for any unlawful use or distribution of this content by users.',
      },
      {
        title: 'Ban on Illegal Use',
        text: 'Any use of the content in violation of your local laws is strictly prohibited, including but not limited to the distribution of child pornography, promotion of violence, or other illegal actions.',
      },
    ],
    disclaimerContinue: 'I have read and continue',
    disclaimerBack: 'Back',
    langRu: 'RU',
    langEn: 'EN',
  },
};

function AgeVerification({ user, userStatus, onVerified, language = 'ru', onLanguageChange }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  const t = (key) => TRANSLATIONS[language]?.[key] ?? TRANSLATIONS.ru[key];

  const handleAgeConfirmation = (confirmed) => {
    if (!confirmed) {
      setError(t('errorText'));
      return;
    }

    console.log('Age confirmation accepted, showing disclaimer...');
    setError(null);
    setShowDisclaimer(true);
  };

  const handleContinueAfterDisclaimer = async () => {
    if (!user?.initData) {
      console.warn('No user initData found during age verification, redirecting without API call');
      navigate('/characters', { replace: true });
      return;
    }

    const requestData = {
      initData: user.initData,
      confirmed: true
    };

    console.log('Sending age verification request after disclaimer:', requestData);
    console.log('User object:', user);

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post('/age_verify', requestData);
      console.log('Age verification response:', response.data);

      if (onVerified) {
        console.log('Calling onVerified callback...');
        await onVerified();
        console.log('onVerified callback completed');
      } else {
        console.log('onVerified callback not provided');
      }

      console.log('Redirecting to characters after successful disclaimer confirmation...');
      navigate('/characters', { replace: true });
    } catch (error) {
      console.error('Age verification error after disclaimer:', error);
      setError(language === 'ru' ? 'Ошибка при подтверждении возраста. Попробуйте еще раз.' : 'Error confirming age. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (showDisclaimer) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-black via-dark-900 to-burgundy-900 p-4">
        <div className="bg-dark-800 rounded-lg shadow-2xl p-8 max-w-2xl w-full border border-burgundy-800 text-left">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-100">{t('disclaimerTitle')}</h1>
            {onLanguageChange && (
              <div className="flex space-x-2">
                <button
                  onClick={() => onLanguageChange('ru')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    language === 'ru'
                      ? 'bg-burgundy-700 text-white'
                      : 'bg-dark-700 text-gray-400 hover:text-white'
                  }`}
                >
                  {t('langRu')}
                </button>
                <button
                  onClick={() => onLanguageChange('en')}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    language === 'en'
                      ? 'bg-burgundy-700 text-white'
                      : 'bg-dark-700 text-gray-400 hover:text-white'
                  }`}
                >
                  {t('langEn')}
                </button>
              </div>
            )}
          </div>

          <p className="text-gray-300 mb-6">{t('disclaimerIntro')}</p>

          <div className="space-y-4 mb-8">
            {(t('disclaimerItems') || []).map((item, index) => (
              <div key={index} className="bg-dark-900 bg-opacity-60 border border-burgundy-800 rounded-lg p-4">
                <h2 className="text-lg font-semibold text-burgundy-400 mb-2">{item.title}</h2>
                <p className="text-gray-300 text-sm leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>

          {error && (
            <div className="mb-6 p-3 bg-burgundy-900 bg-opacity-50 border border-burgundy-600 text-burgundy-400 rounded">
              {error}
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:justify-end sm:space-x-3 space-y-3 sm:space-y-0">
            <button
              onClick={handleContinueAfterDisclaimer}
              disabled={loading}
              className="w-full sm:w-auto bg-burgundy-700 hover:bg-burgundy-600 disabled:bg-gray-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 shadow-lg hover:shadow-burgundy-500/50"
            >
              {loading ? t('confirmingButton') : t('disclaimerContinue')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-black via-dark-900 to-burgundy-900 p-4">
      <div className="bg-dark-800 rounded-lg shadow-2xl p-8 max-w-md w-full text-center border border-burgundy-800">
        {onLanguageChange && (
          <div className="flex justify-end mb-4">
            <div className="flex space-x-2">
              <button
                onClick={() => onLanguageChange('ru')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  language === 'ru'
                    ? 'bg-burgundy-700 text-white'
                    : 'bg-dark-700 text-gray-400 hover:text-white'
                }`}
              >
                {t('langRu')}
              </button>
              <button
                onClick={() => onLanguageChange('en')}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  language === 'en'
                    ? 'bg-burgundy-700 text-white'
                    : 'bg-dark-700 text-gray-400 hover:text-white'
                }`}
              >
                {t('langEn')}
              </button>
            </div>
          </div>
        )}

        <div className="mb-6">
          <div className="w-16 h-16 bg-burgundy-900 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-2xl">🔞</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-100 mb-2">
            {t('title')}
          </h1>
          <p className="text-gray-400">
            {t('subtitle')}
          </p>
        </div>

        <div className="mb-6 p-4 bg-burgundy-900 bg-opacity-30 rounded-lg border border-burgundy-700">
          <p className="text-sm text-gray-300">
            <strong className="text-burgundy-400">{t('warningPrefix')}</strong> {t('warningText')}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-burgundy-900 bg-opacity-50 border border-burgundy-600 text-burgundy-400 rounded">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={() => handleAgeConfirmation(true)}
            disabled={loading}
            className="w-full bg-burgundy-700 hover:bg-burgundy-600 disabled:bg-gray-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 shadow-lg hover:shadow-burgundy-500/50"
          >
            {loading ? t('confirmingButton') : t('confirmButton')}
          </button>

          <button
            onClick={() => handleAgeConfirmation(false)}
            disabled={loading}
            className="w-full bg-dark-700 hover:bg-dark-900 disabled:bg-gray-700 text-gray-300 font-bold py-3 px-6 rounded-lg transition-colors border border-gray-700"
          >
            {t('declineButton')}
          </button>
        </div>

        <div className="mt-6 text-xs text-gray-500">
          <p className="text-gray-400">{t('confirmRequirementsTitle')}</p>
          <ul className="text-left mt-2 space-y-1 text-gray-500">
            {(t('confirmRequirementItems') || []).map((item, index) => (
              <li key={index}>• {item}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default AgeVerification;

